import React, { useState, useEffect } from 'react';
import { useEducationalCanister } from '../hooks/useEducationalCanister';
import { LearningDashboard } from './LearningDashboard';

// Import all tutorial components
import OptionsFundamentals from './tutorials/OptionsFundamentals';
import RiskManagement from './tutorials/RiskManagement';
import OptionsSpreads from './tutorials/OptionsSpreads';
import VolatilityTrading from './tutorials/VolatilityTrading';
import PortfolioHedging from './tutorials/PortfolioHedging';
import AdvancedGreeks from './tutorials/AdvancedGreeks';
import InstitutionalStrategies from './tutorials/InstitutionalStrategies';
import AlgorithmicOptions from './tutorials/AlgorithmicOptions';
import ExoticOptions from './tutorials/ExoticOptions';

export const EducationalDemo = ({ isOpen, onClose }) => {
  // PRESERVE ALL EXISTING STATE MANAGEMENT - ZERO DISRUPTION
  const [currentTutorial, setCurrentTutorial] = useState('options_fundamentals');
  const [currentLesson, setCurrentLesson] = useState(1);
  const [tutorialComplete, setTutorialComplete] = useState(false);
  const [showLearningDashboard, setShowLearningDashboard] = useState(false);
  const [userStats, setUserStats] = useState({
    user_id: "advanced_learner",
    total_xp: 0,
    current_level: "Novice",
    completed_tutorials: [],
    completed_lessons: [],
    badges: [],
    certification_progress: 0,
    streak_days: 1,
    global_rank: 999
  });

  // PRESERVE ALL EXISTING TUTORIAL STATES - ZERO DISRUPTION
  const [tutorialStates, setTutorialStates] = useState({
    options_fundamentals: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    risk_management_basics: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    options_spreads: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    volatility_trading: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    portfolio_hedging: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    advanced_greeks: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    institutional_strategies: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    algorithmic_options: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 },
    exotic_options: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 }
  });

  const [tutorialResult, setTutorialResult] = useState(null);
  
  // PRESERVE EXISTING CANISTER INTEGRATION - ZERO DISRUPTION
  const { completeTutorial, connected, backendStatus } = useEducationalCanister();

  console.log('🎓 FIXED TUTORIAL SYSTEM:', { currentTutorial, currentLesson, showLearningDashboard });

  // FIXED LAUNCH HANDLER - PROPERLY NAVIGATES TO TUTORIAL CONTENT
  const launchSpecificTutorial = (tutorialId) => {
    console.log('🎓 LAUNCHING SPECIFIC TUTORIAL:', tutorialId);
    setCurrentTutorial(tutorialId);
    setCurrentLesson(1);
    setTutorialComplete(false);
    setShowLearningDashboard(false); // CRITICAL: Close dashboard to show tutorial
    
    // Reset tutorial state to intro phase
    setTutorialStates(prev => ({
      ...prev,
      [tutorialId]: { phase: 'intro', quizAnswers: {}, practiceResults: [], currentPractice: 0 }
    }));
  };

  // PRESERVE LEARNING DASHBOARD FUNCTIONALITY - ZERO DISRUPTION
  if (showLearningDashboard) {
    return (
      <LearningDashboard 
        isOpen={showLearningDashboard}
        onClose={() => {
          setShowLearningDashboard(false);
          onClose();
        }}
        onLaunchTutorial={launchSpecificTutorial}
        userStats={userStats}
      />
    );
  }

  if (!isOpen) return null;

  // PRESERVE EXISTING TUTORIAL LIBRARY - ZERO DISRUPTION
  const tutorialLibrary = {
    options_fundamentals: {
      title: 'Options Trading Fundamentals',
      modules: 5,
      xp_per_module: 100,
      difficulty: 'Beginner',
      description: 'Master the basics: contracts, calls vs puts, strikes, premiums, and demo trading.'
    },
    risk_management_basics: {
      title: 'Risk Management Essentials', 
      modules: 4,
      xp_per_module: 120,
      difficulty: 'Beginner',
      description: 'Position sizing, stop losses, portfolio protection, and risk-reward optimization.'
    },
    options_spreads: {
      title: 'Options Spreads & Combinations',
      modules: 7,
      xp_per_module: 150,
      difficulty: 'Intermediate', 
      description: 'Vertical spreads, iron condors, butterflies, calendars, and advanced combinations.'
    },
    volatility_trading: {
      title: 'Volatility Trading Strategies',
      modules: 6,
      xp_per_module: 160,
      difficulty: 'Intermediate',
      description: 'Straddles, strangles, volatility skew, term structure, and delta-neutral approaches.'
    },
    portfolio_hedging: {
      title: 'Portfolio Hedging Strategies',
      modules: 5,
      xp_per_module: 140,
      difficulty: 'Intermediate',
      description: 'Protective puts, collar strategies, index hedging, and portfolio insurance techniques.'
    },
    advanced_greeks: {
      title: 'Advanced Greeks & Risk Metrics',
      modules: 8,
      xp_per_module: 180,
      difficulty: 'Advanced',
      description: 'Delta, gamma, theta, vega, rho, complex sensitivities, and dynamic hedging.'
    },
    institutional_strategies: {
      title: 'Institutional Trading Strategies',
      modules: 10,
      xp_per_module: 200,
      difficulty: 'Expert',
      description: 'Professional strategies used by hedge funds, market makers, and institutional traders.'
    },
    algorithmic_options: {
      title: 'Algorithmic Options Trading',
      modules: 9,
      xp_per_module: 220,
      difficulty: 'Expert', 
      description: 'Automated trading systems, backtesting, risk management, and execution algorithms.'
    },
    exotic_options: {
      title: 'Exotic Options & Derivatives',
      modules: 7,
      xp_per_module: 190,
      difficulty: 'Expert',
      description: 'Barrier options, Asian options, binary options, and structured products.'
    }
  };

  const currentTutorialInfo = tutorialLibrary[currentTutorial];
  const currentState = tutorialStates[currentTutorial];

  // PRESERVE EXISTING STATE UPDATE HELPER - ZERO DISRUPTION
  const updateTutorialState = (updates) => {
    setTutorialStates(prev => ({
      ...prev,
      [currentTutorial]: { ...prev[currentTutorial], ...updates }
    }));
  };

  // SHARED PROPS FOR ALL TUTORIAL COMPONENTS - CONSISTENT INTERFACE
  const sharedTutorialProps = {
    currentLesson,
    setCurrentLesson,
    currentState,
    updateTutorialState,
    currentTutorialInfo,
    userStats,
    setUserStats,
    setTutorialComplete,
    tutorialComplete
  };

  // TUTORIAL COMPLETION SCREEN - PRESERVE EXISTING FUNCTIONALITY
  const renderTutorialCompletion = () => {
    return (
      <div className="space-y-6 text-center">
        <div className="mb-8">
          <div className="text-8xl mb-4">🎊</div>
          <h3 className="text-white text-4xl font-bold mb-4">Tutorial Complete!</h3>
          <p className="text-gray-400 text-xl max-w-2xl mx-auto">
            You've successfully completed {currentTutorialInfo.title}!
          </p>
        </div>

        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/50 rounded-lg p-8">
          <h4 className="text-green-400 font-bold text-2xl mb-6">🏆 Achievement Unlocked</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="text-center">
              <div className="text-4xl text-yellow-400 font-bold">+{currentTutorialInfo.xp_per_module * currentTutorialInfo.modules}</div>
              <div className="text-gray-400">XP Earned</div>
            </div>
            <div className="text-center">
              <div className="text-4xl text-green-400 font-bold">{currentTutorialInfo.modules}</div>
              <div className="text-gray-400">Modules</div>
            </div>
            <div className="text-center">
              <div className="text-4xl text-blue-400 font-bold">{userStats.certification_progress}%</div>
              <div className="text-gray-400">Certified</div>
            </div>
            <div className="text-center">
              <div className="text-4xl text-purple-400 font-bold">{userStats.completed_tutorials.length}</div>
              <div className="text-gray-400">Completed</div>
            </div>
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-4 rounded-lg font-bold text-lg transition-colors"
          >
            Return to Trading Platform
          </button>
          <button
            onClick={() => {
              console.log('🎓 OPENING LEARNING DASHBOARD');
              setShowLearningDashboard(true);
            }}
            className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
          >
            Continue Learning Journey
          </button>
        </div>
      </div>
    );
  };

  // TUTORIAL ROUTING - MODULAR APPROACH WITH PROPER NAVIGATION
  const renderCurrentTutorial = () => {
    if (tutorialComplete) {
      return renderTutorialCompletion();
    }

    // Route to appropriate tutorial component
    switch (currentTutorial) {
      case 'options_fundamentals':
        return <OptionsFundamentals {...sharedTutorialProps} />;
      case 'risk_management_basics':
        return <RiskManagement {...sharedTutorialProps} />;
      case 'options_spreads':
        return <OptionsSpreads {...sharedTutorialProps} />;
      case 'volatility_trading':
        return <VolatilityTrading {...sharedTutorialProps} />;
      case 'portfolio_hedging':
        return <PortfolioHedging {...sharedTutorialProps} />;
      case 'advanced_greeks':
        return <AdvancedGreeks {...sharedTutorialProps} />;
      case 'institutional_strategies':
        return <InstitutionalStrategies {...sharedTutorialProps} />;
      case 'algorithmic_options':
        return <AlgorithmicOptions {...sharedTutorialProps} />;
      case 'exotic_options':
        return <ExoticOptions {...sharedTutorialProps} />;
      default:
        console.error('Unknown tutorial:', currentTutorial);
        return (
          <div className="text-center p-8">
            <div className="text-red-400 text-xl mb-4">⚠️ Tutorial Not Found</div>
            <button
              onClick={() => setShowLearningDashboard(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
            >
              Return to Tutorial Library
            </button>
          </div>
        );
    }
  };

  // PRESERVE EXISTING UI STRUCTURE - ZERO DISRUPTION
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-atticus-navy-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-atticus-navy-700">
        {/* PRESERVE EXISTING HEADER - ZERO DISRUPTION */}
        <div className="flex items-center justify-between p-6 border-b border-atticus-navy-700">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-lg">📚</span>
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{currentTutorialInfo.title}</h2>
              <p className="text-gray-400 text-sm">{currentTutorialInfo.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowLearningDashboard(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              📚 Tutorial Library
            </button>
            <div className="text-xs text-gray-500">
              Module {currentLesson} of {currentTutorialInfo.modules} • {backendStatus}
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white text-xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* PRESERVE EXISTING PROGRESS BAR - ZERO DISRUPTION */}
        {!tutorialComplete && (
          <div className="px-6 py-4 bg-atticus-navy-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Tutorial Progress</span>
              <span className="text-white text-sm">Module {currentLesson} of {currentTutorialInfo.modules}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-yellow-400 to-green-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(currentLesson / currentTutorialInfo.modules) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* TUTORIAL CONTENT - MODULAR ROUTING */}
        <div className="p-6">
          {renderCurrentTutorial()}
        </div>
      </div>
    </div>
  );
};
